import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MonitorSmartphone, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Sits in the same reserved slot as CashDropButton. Shows this terminal's RustDesk
// (self-hosted, free) ID for phone-support calls — clicking it never grants access by
// itself, someone here still has to click "Accept" on the RustDesk prompt.
export default function RemoteSupportButton() {
  const isElectron = !!window.electronAPI?.isElectron;
  const [id, setId] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isElectron || !window.electronAPI?.getRemoteSupportId) return;
    let cancelled = false;
    const poll = async () => {
      const v = await window.electronAPI.getRemoteSupportId();
      if (cancelled) return;
      if (v) setId(v); else setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; };
  }, [isElectron]);

  if (!isElectron || !id) return null;

  const copyId = () => {
    navigator.clipboard.writeText(id);
    toast.success("Remote Support ID copied");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="remote-support-button"
        className="px-2 py-1.5 rounded-lg font-bold text-[11px] bg-violet-100 text-violet-800 hover:bg-violet-200 transition-colors flex items-center gap-1"
        title="Remote Support ID"
      >
        <MonitorSmartphone className="w-3.5 h-3.5" /> Support
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="remote-support-dialog">
          <DialogHeader>
            <DialogTitle>Remote Support ID</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">
            Read this to the technician on the phone. You'll still need to click Accept on this screen before they can see or control it.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-lg font-bold text-zinc-900 bg-zinc-100 rounded-lg px-3 py-1.5 select-all" data-testid="remote-support-dialog-id">
              {id}
            </span>
            <Button variant="outline" size="sm" onClick={copyId} data-testid="remote-support-dialog-copy-btn">
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
